import { format, subDays } from 'date-fns/esm'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { selectStorageSpaceData } from '@/features/domain/order'
import { listArchivedOrders } from '@/features/domain/order'
import { useAppDispatch } from '@/store'

import * as Texts from '../../../../../intl'
import { form } from '../../../formState'
import {
  clearSimulationOrderLimitError,
  getSimulationOrderLimitError,
  registerValidationCallback,
} from '../../../hooks/simulationOrderLimitError'
import type {
  CalendarRange,
  SimulationDate,
  SimulationDateType,
} from '../primitives/SimulationDatesPicker/typings'

interface UseSimulationDatesPickerFieldProps {
  today: Date
  firstDayNotArchived: Date
}

export function useSimulationDatesPickerField({
  today,
  firstDayNotArchived,
}: UseSimulationDatesPickerFieldProps) {
  const {
    field,
    errors,
    formApi: { change: onChange },
  } = form.useFormField('simulationDate')

  const {
    api,
    meta: { status },
  } = form.useFormMeta()

  const dispatch = useAppDispatch()
  const { simulationOrders } = useSelector(selectStorageSpaceData)

  const simulationDateError = errors.find(error => error.field === 'simulationDate')

  // Register callback to trigger validation when order limit error is set
  useEffect(() => {
    const unregister = registerValidationCallback(() => {
      api.validate('simulationDate')
    })
    return unregister
  }, [api])

  // Error clearing effect
  useEffect(() => {
    if (status === 'indeterminate') {
      // Clear all simulationDate field errors by their IDs
      const allErrors = api.getErrors()
      Object.keys(allErrors).forEach(errorId => {
        const error = allErrors[errorId]
        if (error.field === 'simulationDate') {
          api.clearError(errorId)
        }
      })
      // Also clear our custom order limit error
      clearSimulationOrderLimitError()
    }
  }, [status, api])

  form.useAddFormValidation(async formValues => {
    const simulationDate = formValues.simulationDate
    if (!simulationDate || simulationDate.type === 'fromScratch') {
      return undefined
    }

    try {
      if (simulationDate.type === 'fromArchived') {
        const formattedDate = format(simulationDate.date, 'yyyyMMdd')
        const archivedOrdersResult = await dispatch(
          listArchivedOrders({
            startDate: formattedDate,
            endDate: formattedDate,
          }),
        )

        if (!listArchivedOrders.fulfilled.match(archivedOrdersResult)) {
          throw new Error(
            archivedOrdersResult.payload?.message ?? 'Failed to check archived orders',
          )
        }

        if (archivedOrdersResult.payload?.[0]?.ordersCount > simulationOrders) {
          return {
            simulationDate: `${Texts.getSimulationValidationOrderLimitText()}`,
          }
        }
      } else if (simulationDate.type === 'fromOperations') {
        // Check for order limit error from submission (error code 603)
        const orderLimitError = getSimulationOrderLimitError()
        if (orderLimitError) {
          return {
            simulationDate: orderLimitError,
          }
        }
      }

      return undefined
    } catch (error) {
      console.error('Error during simulation date validation')
      return undefined
    }
  })

  // Change handlers
  const handleOnChange = (calendarRange: CalendarRange, type: SimulationDateType) => {
    let simulationDate: SimulationDate = {
      type: 'fromScratch',
    }

    switch (type) {
      case 'fromScratch':
        break
      case 'fromOperations':
        simulationDate = {
          type,
          dates: calendarRange,
        }
        break
      case 'fromArchived':
        simulationDate = {
          type,
          date: calendarRange.min,
        }
    }

    onChange('simulationDate', simulationDate)
  }

  const handleOnChangeType = (type: SimulationDateType) => {
    if (field.value.type === type) {
      return null
    }

    let simulationDate: SimulationDate = {
      type: 'fromScratch',
    }

    switch (type) {
      case 'fromScratch':
        break
      case 'fromOperations':
        simulationDate = {
          type: 'fromOperations',
          dates: { min: today, max: today },
        }
        break
      case 'fromArchived':
        simulationDate = {
          type: 'fromArchived',
          date: subDays(firstDayNotArchived, 1),
        }
        break
    }

    onChange('simulationDate', simulationDate)
  }

  return {
    field,
    simulationDateError,
    handleOnChange,
    handleOnChangeType,
  }
}
